import {
    NetworkError,
    generateRouteHandler,
    makeCanonicalUrl,
    shouldRenderContextualMenu,
} from '@jet-app/music-classical/utilities';
import {
    makePlaylistPageIntent,
    type PlaylistPageIntent,
} from './PlaylistPageIntent';
import type { IntentController } from '@jet/environment/dispatching';
import type { RouteProvider } from '@jet/environment/routing';
import type { ObjectGraph } from '@jet/environment/dependencies';
import type { PlaylistPage } from '@jet-app/music-classical/models/pages/playlist';
import { IntentKind } from '@jet-app/music-classical/intents';
import { renderPlaylistPage } from './renderPlaylistPage';
import { requestViewPage } from '@jet-app/music-classical/api/request-view-page';

const canonicalUrlRule = {
    path: '/{storefront}/playlist/{id}',
    query: ['i?'],
};
const pattern =
    /(?:http)s?:\/\/(?:classical.music)\.apple\.com\/(?:(?<storefront>\w{2})\/)?playlist\/(?:.*?\/)?(?<id>[\w\.-]+)(?:\?|$)/;

interface MatchGroups {
    storefront?: string;
    id?: string;
}

export const PlaylistPageIntentController: IntentController<PlaylistPageIntent> &
    RouteProvider = {
    $intentKind: IntentKind.PLAYLIST_PAGE,

    routes: function (objectGraph: ObjectGraph) {
        return [
            {
                rules: [
                    {
                        regex: [pattern],
                        query: ['l?', 'i?'],
                    },
                ],
                handler: (url, handlerParams) => {
                    const regexParameters: MatchGroups =
                        url.toString().match(pattern)?.groups ?? {};

                    return generateRouteHandler(
                        makePlaylistPageIntent,
                        objectGraph,
                    ).apply(this, [
                        url,
                        { ...handlerParams, ...regexParameters },
                    ]);
                },
            },
        ];
    },

    async perform(
        intent: PlaylistPageIntent,
        objectGraph: ObjectGraph,
    ): Promise<PlaylistPage> {
        const prominentItemIdentifier = intent.i;
        const canonicalURL = makeCanonicalUrl(
            canonicalUrlRule,
            intent,
            objectGraph,
        );

        const apiResponse = await requestViewPage(
            `playlist/${intent.id}`,
            objectGraph,
        );

        if (!apiResponse?.body) {
            throw NetworkError.noContent();
        }

        const contextualMenuResponse = await shouldRenderContextualMenu(
            apiResponse.body.userComponentsAction?.url,
            objectGraph,
        );

        const page = renderPlaylistPage(
            canonicalURL,
            apiResponse,
            contextualMenuResponse,
            objectGraph,
            prominentItemIdentifier,
        );

        return page;
    },
};
